/**
 * Static option lists for the Preferences page (issue #424).
 *
 * Timezone/locale lists are intentionally curated (not the full IANA/CLDR sets)
 * for a usable v1 dropdown. The user's currently-resolved browser zone/locale
 * is always merged in by the page so their detected value is selectable even
 * when not in the curated list.
 */

export interface SelectOption {
  value: string;
  label: string;
}

export const THEME_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: 'system', label: 'System (match device)' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** Common IANA timezones grouped roughly by region. */
export const TIMEZONE_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)' },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Pacific/Honolulu)' },
  { value: 'Europe/London', label: 'London (Europe/London)' },
  { value: 'Europe/Paris', label: 'Central European Time (Europe/Paris)' },
  { value: 'Europe/Berlin', label: 'Berlin (Europe/Berlin)' },
  { value: 'Asia/Kolkata', label: 'India (Asia/Kolkata)' },
  { value: 'Asia/Shanghai', label: 'China (Asia/Shanghai)' },
  { value: 'Asia/Tokyo', label: 'Japan (Asia/Tokyo)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia/Sydney)' },
];

/** Common BCP-47 locales for date/number formatting. */
export const LOCALE_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'ja-JP', label: 'Japanese (Japan)' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
];

/**
 * Merge an extra value into an option list when it is not already present,
 * appending a generated label. Used to guarantee the user's detected/stored
 * timezone or locale is always selectable.
 */
export function withValue(
  options: ReadonlyArray<SelectOption>,
  value: string | undefined,
  labelPrefix = ''
): SelectOption[] {
  const list = [...options];
  if (value && !list.some((o) => o.value === value)) {
    list.push({ value, label: labelPrefix ? `${labelPrefix}${value}` : value });
  }
  return list;
}
